import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, Trash2, Clock, CheckCircle, MessageCircle, XCircle, Palette, DollarSign, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  replied: { label: 'Replied', color: 'bg-green-100 text-green-700' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-600' },
};

const CommissionManager = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [notesInput, setNotesInput] = useState('');
  const [viewingImage, setViewingImage] = useState(null);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadInquiries();
  }, [token, navigate]);

  const loadInquiries = async () => {
    try {
      const res = await axios.get(`${API}/commissions`, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/commissions/${id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status: newStatus } : i));
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
    } catch { toast.error('Failed to update status'); }
  };

  const handleSaveNotes = async (id) => {
    try {
      await axios.put(`${API}/commissions/${id}/status`, { admin_notes: notesInput }, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, admin_notes: notesInput } : i));
      toast.success('Notes saved');
    } catch { toast.error('Failed to save notes'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await axios.delete(`${API}/commissions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(prev => prev.filter(i => i.id !== id));
      toast.success('Inquiry deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = inquiries.filter(i => filter === 'all' || i.status === filter);
  const newCount = inquiries.filter(i => i.status === 'new').length;

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="commission-manager-page">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-2 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <div className="mb-8">
          <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight" data-testid="commission-manager-title">Custom Painting Requests</h1>
          {newCount > 0 && <p className="font-sans text-sm text-blue-600 mt-1"><Clock size={13} className="inline mr-1" />{newCount} new {newCount === 1 ? 'request' : 'requests'}</p>}
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'new', 'in_progress', 'replied', 'closed'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              data-testid={`filter-${f}`}>
              {f === 'all' ? 'All' : STATUS_CONFIG[f]?.label}{f === 'new' && newCount > 0 ? ` (${newCount})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-28 bg-muted rounded-lg animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Palette size={36} className="mx-auto text-muted-foreground/20 mb-3" />
            <p className="font-sans text-muted-foreground text-sm">No requests {filter !== 'all' ? 'in this category' : 'yet'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(inq => {
              const statusCfg = STATUS_CONFIG[inq.status] || STATUS_CONFIG.new;
              const isExpanded = expandedId === inq.id;
              const refImages = inq.reference_images || [];
              return (
                <div key={inq.id} className={`bg-card border rounded-lg overflow-hidden transition-colors ${inq.status === 'new' ? 'border-blue-200/50' : 'border-border'}`} data-testid={`commission-item-${inq.id}`}>
                  <div className="p-4 sm:p-5 cursor-pointer" onClick={() => { setExpandedId(isExpanded ? null : inq.id); setNotesInput(inq.admin_notes || ''); }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-sans text-sm font-semibold">{inq.name}</span>
                          {inq.painting_type && <span className="text-xs text-accent font-medium">{inq.painting_type}</span>}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusCfg.color}`}>{statusCfg.label}</span>
                          {refImages.length > 0 && <span className="text-[10px] text-violet-600 flex items-center gap-0.5"><Image size={10} /> {refImages.length} ref</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1.5">
                          <span className="flex items-center gap-1"><Mail size={11} /> {inq.email}</span>
                          {inq.phone && <span className="flex items-center gap-1"><Phone size={11} /> {inq.phone}</span>}
                        </div>
                        <p className="font-sans text-sm text-foreground/80 line-clamp-2">{inq.description}</p>
                        <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
                          {inq.size_preference && <span>Size: {inq.size_preference}</span>}
                          {inq.medium_preference && <span>Medium: {inq.medium_preference}</span>}
                          {inq.budget_range && <span className="flex items-center gap-1"><DollarSign size={10} /> {inq.budget_range}</span>}
                          {inq.timeline && <span className="flex items-center gap-1"><Clock size={10} /> {inq.timeline}</span>}
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(inq.id); }} className="p-1.5 rounded text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-commission-${inq.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-border pt-4 space-y-4">
                      <div>
                        <p className="font-sans text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Full Description</p>
                        <p className="font-sans text-sm text-foreground/90 whitespace-pre-wrap">{inq.description}</p>
                      </div>

                      {/* Reference Images */}
                      {refImages.length > 0 && (
                        <div>
                          <p className="font-sans text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Reference Images</p>
                          <div className="flex gap-3 flex-wrap">
                            {refImages.map((img, i) => (
                              <div key={i} className="w-32 h-32 rounded-lg overflow-hidden border border-border cursor-pointer hover:ring-2 hover:ring-accent transition-all"
                                onClick={() => setViewingImage(img.data)} data-testid={`ref-image-${inq.id}-${i}`}>
                                <img src={img.data} alt={img.filename || `Reference ${i+1}`} className="w-full h-full object-cover" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="font-sans text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Update Status</p>
                        <div className="flex gap-2 flex-wrap">
                          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                            <button key={key} onClick={() => handleStatusChange(inq.id, key)}
                              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${inq.status === key ? cfg.color + ' ring-1 ring-offset-1' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                              data-testid={`status-${key}-${inq.id}`}>{cfg.label}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="font-sans text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Admin Notes</p>
                        <Textarea value={notesInput} onChange={e => setNotesInput(e.target.value)} placeholder="Internal notes..." rows={3} data-testid={`notes-input-${inq.id}`} />
                        <Button size="sm" onClick={() => handleSaveNotes(inq.id)} className="mt-2 bg-accent hover:bg-accent/90" data-testid={`save-notes-${inq.id}`}>Save Notes</Button>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Submitted: {new Date(inq.created_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Full-size image viewer */}
      {viewingImage && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setViewingImage(null)}>
          <img src={viewingImage} alt="Reference" className="max-w-full max-h-full object-contain rounded-lg" />
          <button className="absolute top-4 right-4 p-2 bg-white/20 rounded-full text-white hover:bg-white/40" onClick={() => setViewingImage(null)}>
            <XCircle size={24} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CommissionManager;
